'use client';
/* eslint-disable */

import {
   Box,
   Button,
   Flex,
   IconButton,
   Table,
   Tbody,
   Td,
   Text,
   Th,
   Thead,
   Tr,
   useColorModeValue,
   useDisclosure,
   Select,
   Grid,
   useOutsideClick,
   useBreakpointValue,
} from '@chakra-ui/react';
import { DeleteIcon, EditIcon } from '@chakra-ui/icons';
import {
   createColumnHelper,
   flexRender,
   getCoreRowModel,
   getSortedRowModel,
   useReactTable,
} from '@tanstack/react-table';
import 'jspdf-autotable';
import React, { useEffect, useState, useRef } from 'react';
import Card from 'components/card/Card';
import Menu from 'components/menu/MainMenu';
import { SearchBar } from 'components/navbar/searchBar/SearchBar';
import SalesRepresentativeMasterForm from './SalesRepresentativeMasterForm';
import { useDispatch, useSelector } from 'react-redux';
import {
   deleteSalesRepresentative,
   getSalesRepresentative,
   getSingleSalesRepresentative,
} from '../../../../../redux/actions/salesRepresentative';
import DeleteDialog from '../../components/DeleteDialog';
import { downloadPDF, downloadExcel, usePagination, useFilterData } from '../../../../../utils/DataTableUtils';
import nodata from 'assets/img/nfts/no data.svg';
import { GridItem, Input, FormLabel } from '@chakra-ui/react';
import ViewSingleData from '../../components/ViewSingleData';
import { MdNavigateNext, MdNavigateBefore } from 'react-icons/md';
import DynamicTable from '../../components/DynamicDataTable';
import { SearchIcon } from '@chakra-ui/icons';
import { motion } from 'framer-motion';
import { useUser } from 'contexts';
import MobileDataTable from '../../components/MobileDataTable';

const columnHelper = createColumnHelper();
const MotionFlex = motion(Flex);
const MotionSearchIcon = motion(SearchIcon);

export default function SalesRepresentativeMaster({ columnsData }) {
   const backgroundColor = useColorModeValue('white', '#101F4A');
   const [sorting, setSorting] = React.useState([]);
   const dispatch = useDispatch();
   const salesRepresentativeData = useSelector((state) => state.salesRepresentative?.list || []);
   const pagination = useSelector((state) => state.salesRepresentative?.pagination);
   const [data, setData] = React.useState(salesRepresentativeData || []);
   const [currentRow, setCurrentRow] = React.useState(null);
   const [focusedInput, setFocusedInput] = React.useState(null);
   // const [pageSize, setPageSize] = React.useState(10);
   // const [currentPage, setCurrentPage] = React.useState(0);
   const [searchQuery, setSearchQuery] = React.useState('');
   const { isOpen, onOpen, onClose } = useDisclosure();
   const [isDeleteDialogOpen, setDeleteDialogOpen] = React.useState(false); // State to manage delete confirmation
   const [selectedRowId, setSelectedRowId] = React.useState(null);
   const [isEditMode, setIsEditMode] = React.useState(false);
   const [isOpenSingleView, setIsOpenSingleView] = React.useState(false);
   const singleEntity = useSelector((state) => state?.salesRepresentative?.single || {});
   const bg = useColorModeValue('#f2f7f2', '#1a254b');
   const icon = useColorModeValue('#000000', '#ffffff');
   const isMobileOrTablet = useBreakpointValue({ base: true, md: false, lg: false });
   const [currentPage, setCurrentPage] = useState(1);
   const [limit, setLimit] = useState(10);
   const isDesktop = useBreakpointValue({ base: false, md: true });
   const { user } = useUser();
   const Admin = user?.isAdmin;

   const companyId = Number(localStorage.getItem('companyId'));
   const salesRepresentativeRight = Array.isArray(user?.userRights)
      ? user.userRights.find((right) => right.companyId === companyId)?.salesRepresentative
      : null;

   // Use the custom hooks
   const filteredData = useFilterData(data, searchQuery, columnsData); // Filtered data
   const { totalPages, pageSize, getPaginationParams } = usePagination(data, filteredData); // Pagination

   useEffect(() => {
      setCurrentPage(1); // Reset to page 1 whenever searchQuery changes
   }, [searchQuery]);

   useEffect(() => {
      dispatch(
         getSalesRepresentative({
            page: currentPage,
            limit: limit === pagination?.totalProducts ? undefined : limit, // undefined fetches everything
            search: searchQuery,
            sortBy: sorting.length ? sorting[0]?.id : 'id',
            sortOrder: sorting.length ? (sorting[0]?.desc ? 'desc' : 'asc') : 'asc',
         }),
      );
   }, [dispatch, currentPage, limit, searchQuery, sorting]);

   const handlePageChange = (direction) => {
      const newPage = direction === 'next' ? pagination.nextPage : pagination.previousPage;
      if (newPage) setCurrentPage(newPage);
   };

   const handlePageSizeChange = (e) => {
      const value = e.target.value;

      // If "ALL" is selected, set limit to null or totalProducts to fetch everything
      const newLimit = value === 'ALL' ? pagination?.totalProducts : Number(value);

      setLimit(newLimit);
      setCurrentPage(1); // Reset to page 1 to avoid offset issues
   };

   const paginatedData = React.useMemo(() => {
      // If salesRepresentativeData is null or undefined, return an empty array
      if (!salesRepresentativeData) return [];

      if (limit === pagination?.totalProducts) return salesRepresentativeData;

      // Ensure salesRepresentativeData is an array before spreading
      return Array.isArray(salesRepresentativeData) ? salesRepresentativeData : [];
   }, [salesRepresentativeData, pagination?.currentPage, limit, pagination?.totalProducts]);

   useEffect(() => {
      // Ensure salesRepresentativeData is an array before spreading
      setData(Array.isArray(salesRepresentativeData) ? [...salesRepresentativeData] : []);
   }, [salesRepresentativeData]);

   useEffect(() => {
      setData([...salesRepresentativeData]);
   }, [salesRepresentativeData]);

   const handleDelete = async () => {
      if (selectedRowId !== null) {
         try {
            await dispatch(deleteSalesRepresentative(selectedRowId));
            dispatch(
               getSalesRepresentative({
                  page: currentPage,
                  limit: limit,
                  search: searchQuery,
               }),
            );
            setData(data.filter((row) => row.id !== selectedRowId));
            setDeleteDialogOpen(false); // Close the dialog after deletion
         } catch (err) {
            // console.error("Failed to delete Color:", err);
         }
      }
   };

   const handleDeleteClick = (id) => {
      setSelectedRowId(id);
      setDeleteDialogOpen(true);
   };

   const textColor = useColorModeValue('secondaryGray.900', 'white');
   const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
   const [columnVisibility, setColumnVisibility] = React.useState(
      columnsData.reduce((acc, column) => {
         acc[column.id] = true;
         return acc;
      }, {}),
   );

   const columns = columnsData
      .filter((column) => columnVisibility[column.id])
      .map((column) =>
         columnHelper.accessor(column.id, {
            id: column.id,
            header: () => (
               <Text justifyContent="space-between" align="center" fontSize={{ sm: '10px', lg: '12px' }}>
                  {column.header}
               </Text>
            ),
            cell: (info) => <Flex align="center">{info.getValue()}</Flex>,
         }),
      );

   // columns.push(
   //    columnHelper.accessor('id', {
   //       id: 'id',
   //       header: () => null,
   //       cell: (info) => (
   //          <Flex justify="end">
   //             <Box className="edit-icon-container">
   //                <IconButton
   //                   aria-label="Edit"
   //                   icon={<EditIcon />}
   //                   variant="ghost"
   //                   colorScheme="brand"
   //                   onClick={(e) => {
   //                      e.stopPropagation(); // Prevent the table row click event from being triggered
   //                      setCurrentRow(info.row);
   //                      setIsEditMode(true);
   //                      onOpen();
   //                      setIsOpenSingleView(false);
   //                   }}
   //                />
   //                <IconButton
   //                   aria-label="Delete"
   //                   icon={<DeleteIcon />}
   //                   variant="ghost"
   //                   colorScheme="red"
   //                   onClick={(e) => {
   //                      e.stopPropagation(); // Prevent the table row click event from being triggered
   //                      handleDeleteClick(info.row.id);
   //                      setIsOpenSingleView(false);
   //                   }}
   //                />
   //             </Box>
   //          </Flex>
   //       ),
   //    }),
   // );

   const table = useReactTable({
      data,
      columns,
      state: { sorting },
      onSortingChange: setSorting,
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
   });

   const handleSave = (formData) => {
      if (isEditMode && currentRow) {
         setData(data.map((row) => (row.id === currentRow.id ? { ...currentRow, ...formData } : row)));
      } else if (!isEditMode) {
         setData([...data, { ...formData, id: Date.now() }]);
      }
      onClose();
   };

   const handleViewSingleData = async (data) => {
      await dispatch(getSingleSalesRepresentative(data.id));
      setIsOpenSingleView(true);
   };
   var dynamicArray = [];
   dynamicArray = [
      { label: 'Name', value: singleEntity?.name },
      { label: 'Mobile', value: singleEntity?.mobile },
   ];

   const [expanded, setExpanded] = useState(false);
   const wrapperRef = useRef();
   useOutsideClick({
      ref: wrapperRef,
      handler: () => {
         if (searchQuery === '') setExpanded(false);
      },
   });

   return (
      <Card
         flexDirection="column"
         w="100%"
         px="0px"
         overflowX={{ sm: 'scroll', lg: 'hidden' }}
         position="relative"
         py="10px"
         borderRadius="7px">
         <Flex
            width={{ base: '100%', md: 'auto' }}
            mb="8px"
            px={{ base: '1px', md: '25px' }}
            justifyContent="space-between"
            align="center"
            overflowX="auto"
            flexWrap="nowrap"
            gap={3}>
            {/* Left Section: Title + Add New Button */}
            <Flex align="center" gap={4} flexShrink={0}>
               <Text
                  color={textColor}
                  ml={{ base: '8px', md: 0 }}
                  fontSize={{ base: '17px', lg: '22px' }}
                  fontWeight="700"
                  lineHeight="100%">
                  Sale - Representative
               </Text>
               {!isMobileOrTablet && (Admin || salesRepresentativeRight?.add) && (
                  <Button
                     onClick={() => {
                        setCurrentRow({});
                        setIsEditMode(false);
                        onOpen();
                     }}
                     variant="darkBrand"
                     color="white"
                     fontSize="sm"
                     fontWeight="500"
                     borderRadius="full"
                     p={{ base: '4', lg: '5' }}>
                     Add New
                  </Button>
               )}
            </Flex>

            {/* Right Section: Pagination + Limit + Search + Menu */}
            <Flex align="center" gap={2} flexShrink={0}>
               {/* Pagination Controls */}
               <Flex align="center" flexShrink={0}>
                  <Box
                     onClick={() => pagination?.hasPreviousPage && handlePageChange('prev')}
                     cursor={pagination?.hasPreviousPage ? 'pointer' : 'not-allowed'}
                     opacity={pagination?.hasPreviousPage ? 1 : 0.5}
                     role="button"
                     aria-label="Previous page">
                     <MdNavigateBefore size={28} />
                  </Box>

                  <Text mx="10px" fontSize={{ base: '14px', lg: '17px' }}>
                     {pagination?.currentPage || 1} / {pagination?.totalPages || 1}
                  </Text>

                  <Box
                     onClick={() => pagination?.hasNextPage && handlePageChange('next')}
                     cursor={pagination?.hasNextPage ? 'pointer' : 'not-allowed'}
                     opacity={pagination?.hasNextPage ? 1 : 0.5}
                     role="button"
                     aria-label="Next page">
                     <MdNavigateNext size={28} />
                  </Box>
               </Flex>

               {/* Page Size Select */}
               <Select
                  width="auto"
                  h={{ base: 9, lg: 10 }}
                  marginRight="5"
                  value={limit === filteredData?.length ? 'ALL' : limit.toString()}
                  onChange={handlePageSizeChange}>
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="30">30</option>
                  <option value="ALL">All</option>
               </Select>

               {/* Search Field */}
               <MotionFlex
                  ref={wrapperRef}
                  align="center"
                  bg={bg}
                  borderRadius="full"
                  cursor="pointer"
                  px={expanded ? 3 : 0}
                  w={expanded ? '200px' : '40px'}
                  h="40px"
                  transition="all 0.3s ease"
                  onClick={() => setExpanded(true)}>
                  <MotionSearchIcon
                     color={icon}
                     animate={{ rotate: expanded ? 0 : 90 }}
                     transition={{ duration: 0.3 }}
                     style={{
                        marginRight: expanded ? '8px' : '10px',
                        marginLeft: expanded ? '0' : '10px',
                     }}
                  />
                  {expanded && (
                     <Input
                        variant="unstyled"
                        placeholder="Search..."
                        value={searchQuery}
                        color={icon}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        autoFocus
                     />
                  )}
               </MotionFlex>

               {/* Download + Column Visibility Menu */}
               <Menu
                  onDownloadPDF={downloadPDF}
                  onDownloadExcel={downloadExcel}
                  columnVisibility={columnVisibility}
                  setColumnVisibility={setColumnVisibility}
               />
            </Flex>

            {/* Mobile Floating Add New Button */}
            {isMobileOrTablet && (Admin || salesRepresentativeRight?.add) && (
               <Box position="fixed" bottom="4" right="4" zIndex={999}>
                  <Button
                     onClick={() => {
                        setCurrentRow({});
                        setIsEditMode(false);
                        onOpen();
                     }}
                     variant="darkBrand"
                     color="white"
                     fontSize="sm"
                     fontWeight="500"
                     borderRadius="full"
                     p={{ base: '4', lg: '5' }}
                     boxShadow="lg">
                     Add New
                  </Button>
               </Box>
            )}
         </Flex>

         {/* Table Rendering */}
         {isDesktop ? (
            <DynamicTable
               singleEntityRights={salesRepresentativeRight}
               table={table}
               tableData={paginatedData}
               handleViewSingleData={handleViewSingleData}
               emptyMessage="No sales representative data found"
               onEditClick={(row) => {
                  setCurrentRow(row);
                  setIsEditMode(true);
                  onOpen();
                  setIsOpenSingleView(false);
               }}
               onDeleteClick={(id) => {
                  handleDeleteClick(id);
                  setIsOpenSingleView(false);
               }}
               canDelete={true}
               columnVisibility={columnVisibility}
            />
         ) : (
            <MobileDataTable
               data={data}
               customerRight={salesRepresentativeRight}
               handleViewSingleData={handleViewSingleData}
               onEditClick={(row) => {
                  setCurrentRow(row);
                  setIsEditMode(true);
                  onOpen();
                  setIsOpenSingleView(false);
               }}
               onDeleteClick={(id) => {
                  handleDeleteClick(id);
                  setIsOpenSingleView(false);
               }}
               emptyMessage={'No sales representative data found'}
            />
         )}
         <SalesRepresentativeMasterForm
            isOpen={isOpen}
            onClose={onClose}
            isEditMode={isEditMode}
            currentRow={currentRow}
            onSave={handleSave}
         />

         <style jsx>{`
            .editable-row {
               position: relative;
               background: ${useColorModeValue('white', 'gray.700')};
               transition: background 0.3s ease;
            }

            .editable-row:hover {
               background: ${useColorModeValue('gray.100', 'gray.600')};
            }

            .editable-row .edit-icon-container {
               display: none;
            }

            .editable-row:hover .edit-icon-container {
               display: block;
               position: absolute;
               right: 10px;
               top: 50%;
               transform: translateY(-50%);
            }
         `}</style>
         <DeleteDialog
            isDeleteDialogOpen={isDeleteDialogOpen}
            setDeleteDialogOpen={setDeleteDialogOpen}
            handleDelete={handleDelete}
            title="Sales Representative"
         />
      </Card>
   );
}
